import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageModalComponent, MessageModalType } from '@shared/components/message-modal/message-modal.component';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface ErrorResponseData {
  statusCode: number;
  message: string;
  detail?: string;
  detailData?: unknown;
  timestamp: string;
  path: string;
}

interface ErrorBloqueioDetailData {
  tipo: 'bloqueio';
  operacao?: 'edicao' | 'exclusao';
  bloqueio?: {
    entidade?: string;
    chave?: string;
    dataIso?: string;
    usuario?: {
      id?: string;
      nome?: string;
      email?: string;
    };
  };
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly isoDateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;

  constructor(public dialog: MatDialog) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((responseError: HttpErrorResponse) => {
        const normalizedError = this.normalizeError(responseError);
        this.openMessageModal(normalizedError);
        return throwError(() => responseError);
      }),
    );
  }

  private normalizeError(responseError: HttpErrorResponse): ErrorResponseData {
    if (this.isRequestTooLargeBySelection(responseError.status)) {
      return {
        statusCode: responseError.status,
        message: 'Você selecionou opções demais para esta consulta. Reduza a quantidade de itens e tente novamente.',
        detail: 'A requisição excedeu o tamanho máximo permitido pelo servidor.',
        timestamp: new Date().toISOString(),
        path: responseError.url ?? '',
      };
    }

    const errorData = responseError.error;

    if (errorData && typeof errorData === 'object' && 'message' in errorData) {
      const backendError = errorData as Partial<ErrorResponseData>;

      return {
        statusCode: backendError.statusCode ?? responseError.status,
        message: backendError.message?.trim() || this.getDefaultMessage(responseError.status),
        detail: this.normalizeDetail(backendError.detail),
        detailData: (backendError as any).detailData,
        timestamp: backendError.timestamp ?? new Date().toISOString(),
        path: backendError.path ?? responseError.url ?? '',
      };
    }

    return {
      statusCode: responseError.status,
      message: this.getDefaultMessage(responseError.status),
      detail: undefined,
      detailData: undefined,
      timestamp: new Date().toISOString(),
      path: responseError.url ?? '',
    };
  }

  private getDefaultMessage(statusCode: number): string {
    if (this.isRequestTooLargeBySelection(statusCode)) {
      return 'A consulta ficou muito grande. Reduza a quantidade de opcoes selecionadas e tente novamente.';
    }

    return 'Nao foi possivel concluir a operacao. Tente novamente.';
  }

  private isRequestTooLargeBySelection(statusCode: number): boolean {
    return statusCode === 414 || statusCode === 431;
  }

  private normalizeDetail(detail?: string): string | undefined {
    if (!detail) {
      return detail;
    }

    return detail.replace(this.isoDateRegex, (isoDate) => this.formatIsoDate(isoDate));
  }

  private getAdditionalNote(error: ErrorResponseData): string | undefined {
    const detailData = this.extractBloqueioDetailData(error.detailData);
    if (!detailData) {
      return error.detail;
    }

    const nome = detailData.bloqueio?.usuario?.nome;
    const email = detailData.bloqueio?.usuario?.email;
    const dataIso = detailData.bloqueio?.dataIso;

    const dataFormatada = dataIso ? this.formatIsoDate(dataIso) : undefined;
    if (nome && email && dataFormatada) {
      return `Bloqueado por ${nome} (${email}) em ${dataFormatada}.`;
    }

    if (dataFormatada) {
      return `Bloqueio ativo em ${dataFormatada}.`;
    }

    return error.detail;
  }

  private extractBloqueioDetailData(detailData: unknown): ErrorBloqueioDetailData | undefined {
    if (!detailData || typeof detailData !== 'object') {
      return undefined;
    }

    const candidate = detailData as ErrorBloqueioDetailData;
    if (candidate.tipo !== 'bloqueio') {
      return undefined;
    }

    return candidate;
  }

  private formatIsoDate(isoDate: string): string {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }

    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  openMessageModal(error: ErrorResponseData): void {
    if (!error || error.statusCode === 401) {
      return;
    }

    if (error.message === 'Internal server error') {
      return;
    }

    this.dialog.open(MessageModalComponent, {
      data: {
        title: 'Falha na operação',
        message: error.message,
        additionalNote: this.getAdditionalNote(error),
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        showCancel: false,
        type: MessageModalType.ERROR,
      },
    });
  }
}
